import React, { useState } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography as MuiTypography,
  LinearProgress,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { useMutation, useQuery } from "@apollo/client";
import { navigate, RouteComponentProps } from "@reach/router";
import queryString from "query-string";
import { gql } from "@apollo/client";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import SportsIcon from "@material-ui/icons/Sports";

// GraphQL Mutations
const REGISTER_COACH_WITH_WORKSPACE = gql`
  mutation RegisterCoachWithWorkspace(
    $workspaceId: String!
    $name: String!
    $surname: String!
    $email: String!
    $password: String!
  ) {
    registerCoachWithWorkspace(
      workspaceId: $workspaceId
      name: $name
      surname: $surname
      email: $email
      password: $password
    ) {
      success
      error
      token
      user {
        id
        name
        surname
        email
        role
      }
    }
  }
`;

const GET_WORKSPACE_INFO = gql`
  query GetWorkspaceInfo($workspaceId: String!) {
    workspaceDetails(workspaceId: $workspaceId) {
      id
      description
      owner {
        name
        surname
      }
    }
  }
`;

// Styled Components
const PageContainer = styled("div")(({ theme }: IStyledArguments) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: theme.palette.extra.backgroundGrey,
  padding: theme.spacing(3),
}));

const ContentCard = styled(Card)(({ theme }: IStyledArguments) => ({
  maxWidth: 480,
  width: "100%",
  padding: theme.spacing(4),
  borderRadius: "20px",
  boxShadow: "0 4px 16px rgba(16, 21, 26, 0.1)",
}));

const LogoContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  marginBottom: theme.spacing(3),
}));

const IconWrapper = styled(Box)(({ theme }: IStyledArguments) => ({
  width: 64,
  height: 64,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(2),
  "& svg": {
    fontSize: "2rem",
    color: "#fff",
  },
}));

const StyledTextField = styled(TextField)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(2),
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
  },
}));

const PasswordStrengthBar = styled(LinearProgress)(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  borderRadius: "4px",
  height: 6,
}));

const StrengthContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const RequirementItem = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontSize: "0.85rem",
  marginBottom: theme.spacing(0.5),
  "& svg": {
    fontSize: "1rem",
  },
}));

const ErrorCard = styled(Card)(({ theme }: IStyledArguments) => ({
  maxWidth: 480,
  width: "100%",
  padding: theme.spacing(4),
  borderRadius: "20px",
  boxShadow: "0 4px 16px rgba(16, 21, 26, 0.1)",
  textAlign: "center",
}));

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 6) strength += 25;
  if (password.length >= 12) strength += 25;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 25;
  return strength;
};

const getPasswordStrengthColor = (strength: number): any => {
  if (strength < 50) return "error";
  if (strength < 75) return "warning";
  return "primary";
};

type PageCoachRegistrationProps = Required<
  Pick<RouteComponentProps, "location">
>;

const PageCoachRegistration: React.FC<PageCoachRegistrationProps> = ({
  location,
}) => {
  const params = queryString.parse(location?.search || "");
  const workspaceId = (params.workspace as string) || "";

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  // Get workspace info
  const {
    data: workspaceData,
    loading: loadingWorkspace,
    error: workspaceError,
  } = useQuery(GET_WORKSPACE_INFO, {
    variables: { workspaceId },
    skip: !workspaceId,
  });

  // Register coach mutation
  const [registerCoach, { loading: submitting }] = useMutation(REGISTER_COACH_WITH_WORKSPACE, {
    onCompleted: (data) => {
      if (
        data.registerCoachWithWorkspace.success &&
        data.registerCoachWithWorkspace.token
      ) {
        // Store the authentication token
        localStorage.setItem("authToken", data.registerCoachWithInvitation.token);
        // Redirect to dashboard
        navigate("/dashboard");
      } else {
        setError(
          data.registerCoachWithInvitation.error || "Failed to register"
        );
      }
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate all fields
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    if (!surname.trim()) {
      setError("Surname is required");
      return;
    }

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    // Validate email format
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      setError("Invalid email address");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    registerCoach({
      variables: { 
        workspaceId,
        name: name.trim(),
        surname: surname.trim(),
        email: email.trim(),
        password,
      },
    });
  };

  const passwordStrength = calculatePasswordStrength(password);

  const requirements = {
    minLength: password.length >= 6,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };

  const isPasswordValid = requirements.minLength;
  const isFormValid = 
    name.trim() && 
    surname.trim() && 
    email.trim() && 
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email) &&
    isPasswordValid && 
    password === confirmPassword;

  // Loading state
  if (!workspaceId) {
    return (
      <PageContainer>
        <ErrorCard>
          <IconWrapper style={{ background: "#f44336" }}>
            <ErrorIcon />
          </IconWrapper>
          <MuiTypography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
            Invalid Link
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            This coach invitation link is invalid or malformed.
          </MuiTypography>
          <MuiTypography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 16 }}
          >
            Please contact your workspace administrator for a new invitation.
          </MuiTypography>
        </ErrorCard>
      </PageContainer>
    );
  }

  if (loadingWorkspace) {
    return (
      <PageContainer>
        <ContentCard>
          <LogoContainer>
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          </LogoContainer>
          <MuiTypography variant="body1" align="center" color="textSecondary">
            Loading workspace information...
          </MuiTypography>
        </ContentCard>
      </PageContainer>
    );
  }

  if (workspaceError || !workspaceData?.workspaceDetails) {
    return (
      <PageContainer>
        <ErrorCard>
          <IconWrapper style={{ background: "#f44336" }}>
            <ErrorIcon />
          </IconWrapper>
          <MuiTypography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
            Workspace Not Found
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            The workspace you're trying to join doesn't exist or is no longer available.
          </MuiTypography>
          <MuiTypography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 16 }}
          >
            Please contact your workspace administrator for assistance.
          </MuiTypography>
        </ErrorCard>
      </PageContainer>
    );
  }

  const workspaceInfo = workspaceData?.workspaceDetails;

  return (
    <PageContainer>
      <ContentCard>
        <LogoContainer>
          <IconWrapper>
            <SportsIcon />
          </IconWrapper>
          <MuiTypography variant="h5" style={{ fontWeight: 600, marginBottom: 8 }}>
            Join {workspaceInfo.description || "Workspace"}
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            Create your coach account
          </MuiTypography>
        </LogoContainer>

        <form onSubmit={handleSubmit}>
          {/* Name Field */}
          <StyledTextField
            fullWidth
            label="First Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            required
            placeholder="Enter your first name"
          />

          {/* Surname Field */}
          <StyledTextField
            fullWidth
            label="Last Name"
            type="text"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            variant="outlined"
            required
            placeholder="Enter your last name"
          />

          {/* Email Field */}
          <StyledTextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            required
            placeholder="your.email@example.com"
          />

          {/* Password Field */}
          <StyledTextField
            fullWidth
            label="Create Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
            required
            placeholder="At least 6 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {password && (
            <StrengthContainer>
              <PasswordStrengthBar
                variant="determinate"
                value={passwordStrength}
                color={getPasswordStrengthColor(passwordStrength)}
              />
              <Box display="flex" fontSize="0.75rem" style={{ gap: "8px" }}>
                <RequirementItem
                  style={{
                    color: requirements.minLength ? "#4caf50" : "#999",
                  }}
                >
                  {requirements.minLength ? (
                    <CheckCircleIcon style={{ color: "#4caf50" }} />
                  ) : (
                    <ErrorIcon style={{ color: "#999" }} />
                  )}
                  6+ characters
                </RequirementItem>
                <RequirementItem
                  style={{
                    color: requirements.hasUpperCase ? "#4caf50" : "#999",
                  }}
                >
                  {requirements.hasUpperCase ? (
                    <CheckCircleIcon style={{ color: "#4caf50" }} />
                  ) : (
                    <ErrorIcon style={{ color: "#999" }} />
                  )}
                  Uppercase
                </RequirementItem>
                <RequirementItem
                  style={{
                    color: requirements.hasNumber ? "#4caf50" : "#999",
                  }}
                >
                  {requirements.hasNumber ? (
                    <CheckCircleIcon style={{ color: "#4caf50" }} />
                  ) : (
                    <ErrorIcon style={{ color: "#999" }} />
                  )}
                  Number
                </RequirementItem>
              </Box>
            </StrengthContainer>
          )}

          {/* Confirm Password Field */}
          <StyledTextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            variant="outlined"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {password && confirmPassword && password !== confirmPassword && (
            <MuiTypography
              variant="caption"
              color="error"
              style={{ display: "block", marginBottom: 16 }}
            >
              Passwords do not match
            </MuiTypography>
          )}

          {error && (
            <MuiTypography
              variant="caption"
              color="error"
              style={{ display: "block", marginBottom: 16 }}
            >
              {error}
            </MuiTypography>
          )}

          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={!isFormValid || submitting}
            style={{ padding: "12px", marginTop: 16, fontWeight: 600 }}
          >
            {submitting ? "Creating Account..." : "Create Account & Join"}
          </Button>
        </form>

        <MuiTypography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: 24, textAlign: "center" }}
        >
          By registering, you agree to the Terms and Conditions
        </MuiTypography>
      </ContentCard>
    </PageContainer>
  );
};

export default PageCoachRegistration;
